import { hasAnyRole, type Role } from "@packages/core/session/roles.ts";
import type { FireflyRuntime } from "@squide/firefly";
import type { i18n } from "i18next";
import type { ComponentType, ReactElement } from "react";
import { Navigate } from "react-router";
import { NavigationItemLabel } from "../i18n/NavigationItemLabel.tsx";
import { useSession } from "../session/SessionContext.tsx";
import { getCommandMenuLinksRegistryPlugin } from "./command-menu/CommandMenuLinksPlugin.ts";
import type { CommandMenuLink } from "./command-menu/CommandMenuLinksRegistry.ts";
import type { LinkAdditionalProps, LinkIcon } from "./link.ts";
import type { AccessHandle, PageTitleHandle, RouteHandle, SidebarHandle } from "./layoutHandles.ts";
import { ADMIN_MENU_ID, areaSidebarMenuId, TOP_MENU_ID, USER_MENU_ID } from "./menuIds.ts";

const NAVIGATION_NAMESPACE = "Navigation";

interface AreaRouteOptions {
    index?: boolean;
    path?: string;
    landing?: boolean;
    access?: readonly Role[];
    page?: string;
    noHeading?: boolean;
    lazy: () => Promise<{ element: ReactElement }>;
}

interface TabRouteOptions extends AreaRouteOptions {
    fullHeight?: boolean;
}

interface TabsAreaOptions {
    landing?: boolean;
    access?: readonly Role[];
    preserveSearchParams?: ReadonlyArray<string>;
    Heading?: ComponentType;
    hideTitle?: boolean;
    errorElement?: ReactElement;
    commandMenu?: boolean;
    useIsTabHidden?: () => (tabId: string) => boolean;
    useRenderAsTitleOnly?: () => boolean;
    useTabsPending?: () => boolean;
    useTabBadgeCount?: () => (tabId: string) => number | undefined;
}

interface RootTabsAreaOptions {
    segment?: string;
    preserveSearchParams?: ReadonlyArray<string>;
    Heading?: ComponentType;
    hideTitle?: boolean;
    errorElement?: ReactElement;
    commandMenu?: boolean;
    useIsTabHidden?: () => (tabId: string) => boolean;
    useRenderAsTitleOnly?: () => boolean;
    useTabsPending?: () => boolean;
    useTabBadgeCount?: () => (tabId: string) => number | undefined;
}

interface AreaLinkOptions {
    priority: number;
    icon?: LinkIcon;
    commandMenu?: boolean;
}

interface TabLinkOptions extends AreaLinkOptions {
    showLock?: boolean;
}

interface NavGroupOptions {
    path: string;
}

interface AreaMenuEntryOptions {
    priority?: number;
}

interface AreaOverlayOptions {
    segment: string;
    page?: string;
    lazy: () => Promise<{ element: ReactElement }>;
}

interface AreaPageOptions {
    index?: boolean;
    lazy: () => Promise<{ element: ReactElement; errorElement?: ReactElement }>;
    page?: string;
    fullHeight?: boolean;
}

interface RootNode {
    readonly kind: "root";
    readonly parent: undefined;
    readonly id: string;
    readonly segment: string;
    readonly scope: AreaScope;
}

interface SidebarNode {
    readonly kind: "sidebar";
    readonly parent: RootNode;
    readonly id: string;
    readonly sectionPriority: number;
    readonly hideLabel: boolean;
}

interface TabsNode {
    readonly kind: "tabs";
    readonly parent: RootNode | SidebarNode | GroupNode;
    readonly id: string;
    readonly segment: string | undefined;
    readonly isLanding: boolean;
    readonly preserveSearchParams: ReadonlyArray<string> | undefined;
    readonly Heading: ComponentType | undefined;
    readonly hideTitle: boolean;
    readonly errorElement?: ReactElement;
    readonly commandMenu: boolean;
    readonly useIsTabHidden: (() => (tabId: string) => boolean) | undefined;
    readonly useRenderAsTitleOnly: (() => boolean) | undefined;
    readonly useTabsPending: (() => boolean) | undefined;
    readonly useTabBadgeCount: (() => (tabId: string) => number | undefined) | undefined;
    readonly access: AccessHandle | undefined;
}

interface GroupNode {
    readonly kind: "group";
    readonly parent: SidebarNode;
    readonly id: string;
    readonly to: string;
}

interface RouteNode {
    readonly kind: "route";
    readonly parent: RootNode | SidebarNode | TabsNode | GroupNode;
    readonly id: string;
    readonly segment: string | undefined;
    readonly isIndex: boolean;
    readonly isLanding: boolean;
    readonly page: string | undefined;
    readonly noHeading: boolean;
    readonly fullHeight: boolean;
    readonly access: AccessHandle | undefined;
}

interface OverlayNode {
    readonly kind: "overlay";
    readonly parent: RootNode | RouteNode;
    readonly id: string;
    readonly segment: string;
    readonly page: string | undefined;
}

type AreaNode = RootNode | SidebarNode | TabsNode | RouteNode | GroupNode | OverlayNode;

interface AreaScope {
    readonly runtime: FireflyRuntime;
    readonly instance: i18n;
    readonly rootIcon: LinkIcon;
    readonly access: AccessHandle | undefined;
    sidebarCount: number;
    landingRedirectRegistered: boolean;
    readonly landings: Array<TabsNode | RouteNode>;
    readonly registeredSections: Set<string>;
}

interface RouteBuilder {
    registerLink: (options: AreaLinkOptions) => void;
    registerOverlay: (id: string, options: AreaOverlayOptions) => void;
}

interface TabRouteBuilder extends RouteBuilder {
    registerLink: (options: TabLinkOptions) => void;
}

export interface TabsAreaBuilder {
    registerRoute: (id: string, options: TabRouteOptions) => TabRouteBuilder;
    registerLink: (options: AreaLinkOptions) => void;
}

export interface NavGroupBuilder {
    registerRoute: (id: string, options: AreaRouteOptions) => RouteBuilder;
    registerTabsArea: (id: string, segment: string, options?: TabsAreaOptions) => TabsAreaBuilder;
    registerLink: (options: AreaLinkOptions) => void;
}

interface SidebarAreaBuilder {
    registerTabsArea: (id: string, segment: string, options?: TabsAreaOptions) => TabsAreaBuilder;
    registerRoute: (id: string, options: AreaRouteOptions) => RouteBuilder;
    registerNavGroup: (id: string, options: NavGroupOptions) => NavGroupBuilder;
    registerLink: (options: AreaLinkOptions) => void;
}

interface SidebarAreaOptions {
    hideLabel?: boolean;
}

interface RootAreaBuilder {
    registerSidebarArea: (id: string, options?: SidebarAreaOptions) => SidebarAreaBuilder;
    registerTabsArea: (id: string, options?: RootTabsAreaOptions) => TabsAreaBuilder;
    registerRoute: (id: string, options: AreaRouteOptions) => RouteBuilder;
    registerPage: (options: AreaPageOptions) => void;
    registerOverlay: (id: string, options: AreaOverlayOptions) => void;
    registerToAdminMenu: (options?: AreaMenuEntryOptions) => void;
    registerToTopNav: (options?: AreaMenuEntryOptions) => void;
    registerToUserMenu: (options?: AreaMenuEntryOptions) => void;
}

function joinPath(base: string, segment: string) {
    const left = base.endsWith("/") ? base.slice(0, -1) : base;
    const right = segment.startsWith("/") ? segment.slice(1) : segment;

    return `${left}/${right}`;
}

function rootOf(node: AreaNode): RootNode {
    let current: AreaNode = node;

    while (current.kind !== "root") {
        current = current.parent;
    }

    return current;
}

const rootId = (node: AreaNode) => rootOf(node).id;
const scopeOf = (node: AreaNode) => rootOf(node).scope;

function resolvedPath(node: AreaNode): string {
    if (node.kind === "root") {
        return node.segment;
    }

    const base = resolvedPath(node.parent);

    if (node.kind === "sidebar") {
        return base;
    }

    if (node.kind === "group") {
        return base;
    }

    return node.segment === undefined ? base : joinPath(base, node.segment);
}

function labelIdPath(node: AreaNode): string {
    return node.kind === "root" ? node.id : `${labelIdPath(node.parent)}.${node.id}`;
}

const labelKey = (node: AreaNode) => `${labelIdPath(node)}.label`;
const sidebarMenuId = (node: AreaNode) => areaSidebarMenuId(rootId(node));
const sectionId = (section: SidebarNode) => `${sidebarMenuId(section)}-${section.id}`;

const groupSectionId = (group: GroupNode) => `${sidebarMenuId(group)}-${labelIdPath(group)}`;

function tabsMenuId(tabs: TabsNode) {
    switch (tabs.parent.kind) {
        case "sidebar":
            return `${sectionId(tabs.parent)}-${tabs.id}-tabs`;
        case "group":
            return `${groupSectionId(tabs.parent)}-${tabs.id}-tabs`;
        case "root":
            return `${rootId(tabs)}-${tabs.id}-tabs`;
    }
}

const labelElement = (node: AreaNode): ReactElement => <NavigationItemLabel instance={scopeOf(node).instance} namespace={NAVIGATION_NAMESPACE} resourceKey={labelKey(node)} />;

function sidebarHandle(node: AreaNode): SidebarHandle {
    return { menuId: sidebarMenuId(node), i18next: { instance: scopeOf(node).instance, namespace: NAVIGATION_NAMESPACE, resourceKey: `${rootId(node)}.label` } };
}

function pageTitleHandle(node: AreaNode): PageTitleHandle {
    return { i18next: { instance: scopeOf(node).instance, namespace: NAVIGATION_NAMESPACE, resourceKey: labelKey(node) } };
}

function analyticsHandleField(page: string | undefined): Pick<RouteHandle, "analytics"> {
    return page ? { analytics: { page } } : {};
}

function resolveAccess(node: AreaNode): AccessHandle | undefined {
    for (let current: AreaNode | undefined = node; current; current = current.parent) {
        if ((current.kind === "route" || current.kind === "tabs") && current.access) {
            return current.access;
        }
    }

    return scopeOf(node).access;
}

function accessHandleField(node: AreaNode): Pick<RouteHandle, "access"> {
    const access = resolveAccess(node);

    return access ? { access } : {};
}

function commandMenuI18next(node: AreaNode): CommandMenuLink["i18next"] {
    return {
        instance: scopeOf(node).instance,
        namespace: NAVIGATION_NAMESPACE,
        labelKey: labelKey(node),
        descriptionKey: `${labelIdPath(node)}.description`,
        parentLabelKey: node.kind === "root" ? "" : labelKey(node.parent),
        groupLabelKey: `${rootId(node)}.label`
    };
}

function lockProps(node: AreaNode): Required<Pick<LinkAdditionalProps, "lock">> {
    const { instance } = scopeOf(node);

    return {
        lock: {
            label: { instance, namespace: NAVIGATION_NAMESPACE, resourceKey: `${labelIdPath(node)}.lockLabel` },
            tooltip: { instance, namespace: NAVIGATION_NAMESPACE, resourceKey: `${labelIdPath(node)}.lockTooltip` }
        }
    };
}

function registerNavItem(node: AreaNode, itemId: string, to: string, icon: LinkIcon | undefined, priority: number, target: { menuId: string; sectionId?: string }, options: { commandMenu?: boolean; tabId?: string; showLock?: boolean } = {}): void {
    const { runtime } = scopeOf(node);

    runtime.registerNavigationItem({ $id: itemId, $priority: priority, $label: labelElement(node), to, $additionalProps: { icon, tabId: options.tabId, ...(options.showLock ? lockProps(node) : {}) } satisfies LinkAdditionalProps }, target);

    if (options.commandMenu ?? true) {
        getCommandMenuLinksRegistryPlugin(runtime)?.registerLink({ id: itemId, to, groupId: rootId(node), icon, priority, i18next: commandMenuI18next(node), ...accessHandleField(node) });
    }
}

function ensureSection(section: SidebarNode): void {
    const { runtime, registeredSections } = scopeOf(section);
    const id = sectionId(section);

    if (registeredSections.has(id)) {
        return;
    }

    registeredSections.add(id);

    runtime.registerNavigationItem({ $id: id, $priority: section.sectionPriority, $label: section.hideLabel ? "" : labelElement(section), children: [] }, { menuId: sidebarMenuId(section) });
}

function registerNavGroupSection(group: GroupNode, options: AreaLinkOptions): void {
    ensureSection(group.parent);

    const { runtime } = scopeOf(group);
    const itemId = groupSectionId(group);

    runtime.registerNavigationItem(
        { $id: itemId, $priority: options.priority, $label: labelElement(group), children: [], $additionalProps: { icon: options.icon ?? scopeOf(group).rootIcon, to: group.to } satisfies LinkAdditionalProps },
        { menuId: sidebarMenuId(group), sectionId: sectionId(group.parent) }
    );
}

function LandingRedirect({ root }: { root: RootNode }) {
    const { landings } = root.scope;
    const session = useSession();

    if (landings.length === 0) {
        throw new Error(`[registerArea] area "${root.id}" must mark a node with \`landing: true\` as its landing, found none.`);
    }

    const unscoped = landings.filter(node => !node.access);

    if (landings.length > 1 && unscoped.length > 0) {
        throw new Error(`[registerArea] area "${root.id}" declares ${landings.length} landings, so each must declare \`access\` to say whose it is — ${unscoped.length} does not.`);
    }

    const landing = landings.find(node => node.access && hasAnyRole(session, node.access.roles)) ?? landings[0];

    return <Navigate to={resolvedPath(landing)} replace />;
}

function ensureLandingRedirect(root: RootNode): void {
    const { scope } = root;

    if (scope.landingRedirectRegistered) {
        return;
    }

    scope.landingRedirectRegistered = true;

    scope.runtime.registerRoute({ path: root.segment, element: <LandingRedirect root={root} /> });
}

function registerRouteObject(node: RouteNode, lazy: AreaRouteOptions["lazy"]): void {
    const { runtime } = scopeOf(node);

    if (node.parent.kind === "tabs") {
        const handleFields = { ...analyticsHandleField(node.page), ...accessHandleField(node), ...(node.fullHeight ? { fullHeight: true, pageTitle: pageTitleHandle(node) } : {}) };
        const handle: RouteHandle | undefined = Object.keys(handleFields).length > 0 ? handleFields : undefined;

        runtime.registerRoute(node.isIndex ? { index: true, lazy, handle } : { path: resolvedPath(node), lazy, handle }, { parentPath: resolvedPath(node.parent) });

        return;
    }

    const handle: RouteHandle = {
        sidebar: sidebarHandle(node),
        pageTitle: pageTitleHandle(node),
        ...(node.noHeading ? { noHeading: true } : {}),
        ...analyticsHandleField(node.page),
        ...accessHandleField(node)
    };

    runtime.registerRoute(node.isIndex ? { index: true, lazy, handle } : { path: resolvedPath(node), lazy, handle });
}

function registerOverlayRoute(node: OverlayNode, lazy: AreaOverlayOptions["lazy"]): void {
    const handle: RouteHandle = { preventScrollReset: true, ...analyticsHandleField(node.page), ...accessHandleField(node) };

    scopeOf(node).runtime.registerRoute({ path: resolvedPath(node), lazy, handle }, { parentPath: resolvedPath(node.parent) });
}

function registerTabsPageRoute(node: TabsNode): void {
    const handle: RouteHandle = {
        pageTitle: pageTitleHandle(node),
        tabs: {
            menuId: tabsMenuId(node),
            preserveSearchParams: node.preserveSearchParams,
            Heading: node.Heading,
            hideTitle: node.hideTitle,
            useIsTabHidden: node.useIsTabHidden,
            useRenderAsTitleOnly: node.useRenderAsTitleOnly,
            useTabsPending: node.useTabsPending,
            useTabBadgeCount: node.useTabBadgeCount
        },
        ...(node.parent.kind === "sidebar" || node.parent.kind === "group" ? { sidebar: sidebarHandle(node) } : {}),
        ...accessHandleField(node)
    };

    scopeOf(node).runtime.registerRoute({ path: resolvedPath(node), handle, ...(node.errorElement ? { errorElement: node.errorElement } : {}) });
}

function registerRootMenuEntry(root: RootNode, menuId: string, priority: number): void {
    const { runtime, rootIcon } = root.scope;
    const itemId = `${menuId}-${root.id}`;

    runtime.registerNavigationItem({ $id: itemId, $priority: priority, $label: labelElement(root), to: root.segment, $additionalProps: { icon: rootIcon } satisfies LinkAdditionalProps }, { menuId });

    getCommandMenuLinksRegistryPlugin(runtime)?.registerLink({ id: itemId, to: root.segment, groupId: root.id, icon: rootIcon, priority, menuId, i18next: commandMenuI18next(root), ...accessHandleField(root) });
}

function registerPage(root: RootNode, options: AreaPageOptions): void {
    const handle: RouteHandle = { pageTitle: pageTitleHandle(root), ...(root.scope.sidebarCount > 0 ? { sidebar: sidebarHandle(root) } : {}), ...analyticsHandleField(options.page), ...accessHandleField(root), ...(options.fullHeight ? { fullHeight: true } : {}) };

    root.scope.runtime.registerRoute(options.index ? { index: true, lazy: options.lazy, handle } : { path: root.segment, lazy: options.lazy, handle });
}

function registerRouteLink(node: RouteNode, options: TabLinkOptions): void {
    const icon = node.parent.kind === "group" ? options.icon : (options.icon ?? scopeOf(node).rootIcon);
    const to = resolvedPath(node);

    switch (node.parent.kind) {
        case "tabs": {
            const menuId = tabsMenuId(node.parent);

            registerNavItem(node, `${menuId}-${node.id}`, to, icon, options.priority, { menuId }, { commandMenu: options.commandMenu ?? node.parent.commandMenu, tabId: node.id, showLock: options.showLock });

            return;
        }
        case "sidebar":
            ensureSection(node.parent);
            registerNavItem(node, `${sidebarMenuId(node)}-${labelIdPath(node)}`, to, icon, options.priority, { menuId: sidebarMenuId(node), sectionId: sectionId(node.parent) });

            return;
        case "group": {
            const group = node.parent;

            registerNavItem(node, `${sidebarMenuId(node)}-${labelIdPath(node)}`, to, icon, options.priority, { menuId: sidebarMenuId(node), sectionId: groupSectionId(group) });

            return;
        }
        case "root":
            registerNavItem(node, `${sidebarMenuId(node)}-${labelIdPath(node)}`, to, icon, options.priority, { menuId: sidebarMenuId(node) });
    }
}

function assertLeafSegment(kind: string, id: string, parent: AreaNode, segment: string): void {
    const parentPath = resolvedPath(parent);

    if (parentPath !== "" && (segment === parentPath || segment.startsWith(`${parentPath}/`))) {
        const leaf = segment.slice(parentPath.length) || "/";

        throw new Error(`[registerArea] ${kind} "${id}" path "${segment}" must be a leaf segment, not the full path. It is joined to the parent path ("${parentPath}") automatically — pass just the trailing segment (e.g. "${leaf}").`);
    }
}

function makeRoute(parent: RootNode | SidebarNode | TabsNode | GroupNode, id: string, options: TabRouteOptions): RouteNode {
    if (options.path !== undefined) {
        assertLeafSegment("route", id, parent, options.path);
    }

    const node: RouteNode = {
        kind: "route",
        parent,
        id,
        segment: options.index ? undefined : (options.path ?? id),
        isIndex: options.index ?? false,
        isLanding: options.landing ?? false,
        page: options.page,
        noHeading: options.noHeading ?? false,
        fullHeight: options.fullHeight ?? false,
        access: options.access ? { roles: options.access } : undefined
    };

    if (node.isLanding) {
        scopeOf(node).landings.push(node);
    }

    registerRouteObject(node, options.lazy);

    return node;
}

function registerOverlay(parent: RootNode | RouteNode, id: string, options: AreaOverlayOptions): void {
    assertLeafSegment("overlay", id, parent, options.segment);

    const node: OverlayNode = { kind: "overlay", parent, id, segment: options.segment, page: options.page };

    registerOverlayRoute(node, options.lazy);
}

const createRoute = (node: RouteNode): RouteBuilder => ({
    registerLink: options => registerRouteLink(node, options),
    registerOverlay: (id, options) => registerOverlay(node, id, options)
});

const createTabRoute = (node: RouteNode): TabRouteBuilder => ({
    registerLink: options => registerRouteLink(node, options),
    registerOverlay: (id, options) => registerOverlay(node, id, options)
});

function makeTabsNode(parent: TabsNode["parent"], id: string, segment: string | undefined, isLanding: boolean, options: TabsAreaOptions | RootTabsAreaOptions): TabsNode {
    return {
        kind: "tabs",
        parent,
        id,
        segment,
        isLanding,
        preserveSearchParams: options.preserveSearchParams,
        Heading: options.Heading,
        hideTitle: options.hideTitle ?? false,
        errorElement: options.errorElement,
        commandMenu: options.commandMenu ?? true,
        useIsTabHidden: options.useIsTabHidden,
        useRenderAsTitleOnly: options.useRenderAsTitleOnly,
        useTabsPending: options.useTabsPending,
        useTabBadgeCount: options.useTabBadgeCount,
        access: "access" in options && options.access ? { roles: options.access } : undefined
    };
}

function createTabsArea(node: TabsNode): TabsAreaBuilder {
    registerTabsPageRoute(node);

    if (node.isLanding) {
        scopeOf(node).landings.push(node);
    }

    return {
        registerRoute: (id, options) => createTabRoute(makeRoute(node, id, options)),
        registerLink(options) {
            if (node.parent.kind === "root") {
                throw new Error("[registerArea] a root tabbed area has no sidebar link — register the root to a menu via `registerToTopNav`/`registerToUserMenu` instead.");
            }

            if (node.parent.kind === "group") {
                registerNavItem(node, `${sidebarMenuId(node)}-${labelIdPath(node)}`, resolvedPath(node), options.icon, options.priority, { menuId: sidebarMenuId(node), sectionId: groupSectionId(node.parent) });

                return;
            }

            ensureSection(node.parent);
            registerNavItem(node, `${sidebarMenuId(node)}-${labelIdPath(node)}`, resolvedPath(node), options.icon ?? scopeOf(node).rootIcon, options.priority, { menuId: sidebarMenuId(node), sectionId: sectionId(node.parent) });
        }
    };
}

function createSidebarArea(node: SidebarNode): SidebarAreaBuilder {
    return {
        registerTabsArea: (id, segment, options = {}) => createTabsArea(makeTabsNode(node, id, segment, options.landing ?? false, options)),
        registerRoute: (id, options) => createRoute(makeRoute(node, id, options)),
        registerNavGroup(id, options) {
            const groupNode: GroupNode = { kind: "group", parent: node, id, to: joinPath(resolvedPath(node), options.path) };

            return {
                registerRoute: (childId, childOptions) => createRoute(makeRoute(groupNode, childId, childOptions)),
                registerTabsArea: (childId, segment, childOptions = {}) => createTabsArea(makeTabsNode(groupNode, childId, segment, childOptions.landing ?? false, childOptions)),
                registerLink: linkOptions => registerNavGroupSection(groupNode, linkOptions)
            };
        },
        registerLink(options) {
            ensureSection(node);
            registerNavItem(node, `${sidebarMenuId(node)}-${labelIdPath(node)}`, resolvedPath(node), options.icon ?? scopeOf(node).rootIcon, options.priority, { menuId: sidebarMenuId(node), sectionId: sectionId(node) });
        }
    };
}

function createRoot(node: RootNode): RootAreaBuilder {
    return {
        registerSidebarArea(id, options = {}) {
            ensureLandingRedirect(node);

            const sectionPriority = 1000 - node.scope.sidebarCount * 100;
            node.scope.sidebarCount += 1;

            return createSidebarArea({ kind: "sidebar", parent: node, id, sectionPriority, hideLabel: options.hideLabel ?? false });
        },
        registerTabsArea: (id, options = {}) => createTabsArea(makeTabsNode(node, id, options.segment, false, options)),
        registerRoute: (id, options) => createRoute(makeRoute(node, id, options)),
        registerPage: options => registerPage(node, options),
        registerOverlay: (id, options) => registerOverlay(node, id, options),
        registerToAdminMenu: (options = {}) => registerRootMenuEntry(node, ADMIN_MENU_ID, options.priority ?? 0),
        registerToTopNav: (options = {}) => registerRootMenuEntry(node, TOP_MENU_ID, options.priority ?? 0),
        registerToUserMenu: (options = {}) => registerRootMenuEntry(node, USER_MENU_ID, options.priority ?? 0)
    };
}

export interface RegisterAreaOptions {
    access?: readonly Role[];
}

export function registerArea(runtime: FireflyRuntime, id: string, segment: string, icon: LinkIcon, instance: i18n, options: RegisterAreaOptions = {}): RootAreaBuilder {
    const scope: AreaScope = {
        runtime,
        instance,
        rootIcon: icon,
        access: options.access ? { roles: options.access } : undefined,
        sidebarCount: 0,
        landingRedirectRegistered: false,
        landings: [],
        registeredSections: new Set()
    };

    runtime.registerDeferredRegistrationScopeStartedListener(() => scope.registeredSections.clear());

    return createRoot({ kind: "root", parent: undefined, id, segment, scope });
}
